import { useEffect, useMemo, useRef, useState, type ElementType } from "react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Checkbox } from "../../ui/checkbox";
import { Monitor, ChevronLeft, ChevronRight, Settings, Router, ArrowUp, Loader2 } from "lucide-react";
import StatusIndicator from "./StatusIndicator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Device, DeviceTableProps, RecordingTogglePayload } from "../interfaces/EndpointInterfaces";
import DeviceModeDropdown from "./DeviceModeDropdown";
import useUserStore from "../../../services/userStore";
import PublishComponent from "../PublishComponent";
import { useNavigate } from "react-router-dom";
import { FaWindows, FaApple, FaLinux } from "react-icons/fa";
import { Switch } from "../../ui/switch";
import SSHSettingsDialog from "./SSHSettingsDialog";
import AdminWindowsConnectDialog from "./AdminWindowsConnectDialog";
import DeviceOperatingMode from "./DeviceOperatingMode";
import { getCredentialsList } from "../Credentials/Api/credentialapi";
import { CredentialInterface } from "../Credentials/interfaces/credentialinterface";
import { Skeleton } from "../../ui/skeleton";
import { useToast } from "../../../hooks/use-toast";
import { manageComputer } from "./Api/computerApi";
import { updateTheMachine } from "./Api/EndpointApi";
import { PolicyProfiles } from "../PolicyProfiles/interface";
import { getProfiles } from "../PolicyProfiles/Api/policyprofileapi";
import {
    LIST_STATE_KEYS,
    writePersistedListState,
} from "../../../utils/listPaginationState";
import { DevicePlatform, getDevicePlatform, groupDevicesByPlatform } from "./devicePlatform";

const platformCards = [
  { platform: "windows", label: "Windows", icon: FaWindows },
  { platform: "mac", label: "Mac", icon: FaApple },
  { platform: "linux", label: "Linux", icon: FaLinux },
  { platform: "other", label: "Other devices", icon: Monitor },
] as const;

type DeviceTableSection = {
  platform: DevicePlatform | "all";
  label: string;
  icon: ElementType;
  devices: Device[];
};

export default function DeviceTable({
  isLoading,
  devices,
  onConnect,
  onDeviceSelect,
  selectedTenant,
  onRecordingToggle,
  totalDevicesCount,
  adminAccess,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  configureDeviceUsername,
  fetchDeviceList,
  fetchDeviceCount,
  roles,
  deviceLoadingState,
  lastSelectedDevice,
  platformFilter = null,
  groupByPlatform = false,
}: DeviceTableProps) {
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showSSHDialog, setShowSSHDialog] = useState(false);
  const [selectedDeviceForSSH, setSelectedDeviceForSSH] = useState<Device | null>(null);
  const [showAdminConnectDialog, setShowAdminConnectDialog] = useState(false);
  const [device, setSelectedDevice] = useState<Device | null>(null);
  const [credentials, setCredentials] = useState<CredentialInterface[] | null>(null);
  const [loadingDevices, setLoadingDevices] = useState<{ [key: string]: boolean }>({});
  const [profiles, setProfiles] = useState<PolicyProfiles[]>([]);
  const [profileUpdatingDevices, setProfileUpdatingDevices] = useState<Record<string, boolean>>({});
  const profileUpdateTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const profileUpdateExpiryRef = useRef<Record<string, number>>({});
  const PROFILE_UPDATE_LOCK_MS = 3 * 60 * 1000;
  const PROFILE_UPDATE_STORAGE_KEY = "endpoint_profile_update_expiry";
  const { role, userId } = useUserStore((state) => state.user);
  const [loadIpAddresses, setLoadIpAddresses] = useState<{ [key: string]: boolean }>({});

  const [recordingState, setRecordingState] = useState<{ [key: string]: boolean }>(() => {
    const initial: { [key: string]: boolean } = {};
    devices.forEach((d) => {
      initial[d.id] = !!d.recordingPathEnabled;
    });
    return initial;
  });

  const navigate = useNavigate();
  const { toast } = useToast();
  const totalPages = Math.ceil(totalDevicesCount / rowsPerPage);
  const paginatedDevices = devices;
  const devicesByPlatform = useMemo(() => groupDevicesByPlatform(devices), [devices]);
  const tableSections = useMemo<DeviceTableSection[]>(() => {
    if (groupByPlatform) {
      return platformCards
        .filter(({ platform }) => !platformFilter || platform === platformFilter)
        .map((card) => ({ ...card, devices: devicesByPlatform[card.platform] }));
    }
    const filteredCard = platformCards.find(({ platform }) => platform === platformFilter);
    return [{
      platform: filteredCard?.platform ?? ("all" as const),
      label: filteredCard?.label ?? "All endpoints",
      icon: filteredCard?.icon ?? Monitor,
      devices: platformFilter ? devices.filter((device) => getDevicePlatform(device) === platformFilter) : devices,
    }];
  }, [groupByPlatform, platformFilter, devices, devicesByPlatform]);


  const toggleShowMore = (deviceId: string) => {
    setLoadIpAddresses((prev) => ({
      ...prev,
      [deviceId]: !prev[deviceId],
    }));
  };

  const triggerProfileUpdatingState = (deviceId: string) => {
    const expiryTime = Date.now() + PROFILE_UPDATE_LOCK_MS;
    profileUpdateExpiryRef.current[deviceId] = expiryTime;
    localStorage.setItem(PROFILE_UPDATE_STORAGE_KEY, JSON.stringify(profileUpdateExpiryRef.current));

    setProfileUpdatingDevices((prev) => ({ ...prev, [deviceId]: true }));

    if (profileUpdateTimersRef.current[deviceId]) {
      clearTimeout(profileUpdateTimersRef.current[deviceId]);
    }

    profileUpdateTimersRef.current[deviceId] = setTimeout(() => {
      setProfileUpdatingDevices((prev) => ({ ...prev, [deviceId]: false }));
      delete profileUpdateTimersRef.current[deviceId];
      delete profileUpdateExpiryRef.current[deviceId];
      localStorage.setItem(PROFILE_UPDATE_STORAGE_KEY, JSON.stringify(profileUpdateExpiryRef.current));
    }, PROFILE_UPDATE_LOCK_MS);
  };

  const fetchProfiles = async (tenantName: string) => {
    let response: PolicyProfiles[] = await getProfiles(tenantName);
    if (response) {
      setProfiles(response);
    }
  }

  const handleDeviceClick = (device: Device) => {
    try {
      if (device) {
        writePersistedListState(LIST_STATE_KEYS.endpoints, { page, rowsPerPage });
        navigate(`/endpoints/deviceDetails/${device.id}`, { state: { device, adminAccess } });
      }
    } catch (e) {
      console.log(e)
    }
  };

  const handlePublish = async (deviceId: string, selectedRoles: string[]) => {

    let response: Boolean = await manageComputer(selectedTenant, deviceId, selectedRoles);
    if (response) {
      toast({ title: "Publishing device ", description: selectedRoles.length > 0 ? `Published device to roles` : "Unpublished from all roles" });
      fetchDeviceList?.();
      fetchDeviceCount?.();
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allIds = new Set(paginatedDevices.map((d) => d.id));
      setSelectedDevices(allIds);
      paginatedDevices.forEach((device) => onDeviceSelect?.(device.id, true));
    } else {
      setSelectedDevices(new Set());
      paginatedDevices.forEach((device) => onDeviceSelect?.(device.id, false));
    }
  };

  const handleDeviceSelectSingle = (deviceId: string) => {
    setSelectedDevices((prevSelected) => {
      const newSelected = new Set<string>();

      // If this device was already selected, unselect it
      if (!prevSelected.has(deviceId)) {
        newSelected.add(deviceId); // select only this device
        onDeviceSelect?.(deviceId, true);
      } else {
        onDeviceSelect?.(deviceId, false);
      }

      // Update selectAll only if all devices are selected (unlikely in single-select)
      setSelectAll(false);

      return newSelected;
    });
  };

  const getCredentials = async (device: Device) => {
    const response = await getCredentialsList(selectedTenant, device.id);
    const mergedCredentials: CredentialInterface[] = device.isManuallyAddedMachine
      ? response
      : [{ credentialUsername: "wsadmin", credentialLabel: "wsadmin", createdDate: new Date().toISOString(), id: 0 }, ...response];
    setCredentials(mergedCredentials);
    setShowAdminConnectDialog(true);
  }

  const handleConnect = (device: Device) => {
    setSelectedDevice(device);
    if (isDeviceWindows(device) || device.isManuallyAddedMachine) {
      getCredentials(device);
    } else {
      setShowAdminConnectDialog(false);
      onConnect?.(device, "", "");
    }
  };

  const connectAccessDisable = (device: Device) => {
    switch (adminAccess) {
      case "NO_ACCESS":
        return true;

      case "SERVER_ONLY":
        const os = device.OperatingSystem?.toLowerCase() ?? "";
        if (device?.isManuallyAddedMachine) {
          return false;
        } else if (
          os.includes("linux") ||
          (os.includes("windows") && os.includes("server"))
        ) {
          return false;
        } else {
          return true;
        }

      default:
        return false;
    }
  };


  const connectButtonEnabled = (device: Device) => {
    const isEnabled =
      device.connectionCreatedFor?.some(
        (user) => userId === user.userId && role === user.role
      ) ?? false;

    return isEnabled;
  };

  const getOSIcon = (osString: string) => {
    const os = osString.toLowerCase();
    const sizeClass = "w-7 h-7";

    if (os.includes("windows"))
      return <FaWindows className={`${sizeClass} text-blue-600 dark:text-blue-400`} />;
    if (os.includes("mac") || os.includes("darwin"))
      return <FaApple className={`${sizeClass} text-gray-800 dark:text-white`} />; // white in dark mode
    if (
      os.includes("ubuntu") ||
      os.includes("centos") ||
      os.includes("rhel") ||
      os.includes("rocky") ||
      os.includes("linux")
    )
      return <FaLinux className={`${sizeClass} text-black dark:text-white`} />; // white in dark mode

    return <FaLinux className={`${sizeClass} text-gray-500 dark:text-gray-400`} />;
  };

  const upgradeMachine = async (computerId: string) => {
    setLoadingDevices((prev) => ({ ...prev, [computerId]: true }));

    try {
      await updateTheMachine(computerId);
      toast({ title: "Upgrading device ", description: `Upgrading the agent version` });

    } catch (error) {
      console.error('Error upgrading the machine:', error);
      toast({ title: "Error upgrading the machine", variant: "destructive" });
      setLoadingDevices((prev) => ({ ...prev, [computerId]: false }));


    }
  };

  const isDeviceWindows = (device: Device) => {
    if (device.os?.trim().length > 0) {
      return device.os.toLowerCase().includes("windows");
    } else {
      return true;
    }
  };

  const handleRecordingChange = (deviceId: string, recording: boolean, device: Device) => {
    setRecordingState((prev) => ({
      ...prev,
      [deviceId]: recording,
    }));
    const payload: RecordingTogglePayload = {
      deviceId,
      deviceName: device?.name,
      recording,
      timestamp: new Date().toISOString(),
      selectedTime: device?.accessRemainingTime,
      action: "TOGGLE_DEVICE_RECORDING",
      device
    };
    try {
      onRecordingToggle?.(payload);
    } catch (err) {
      console.error("Recording toggle failed:", err);
      setRecordingState((prev) => ({
        ...prev,
        [deviceId]: !recording,
      }));
    }
  };
  // pagination visible pages with ellipsis
  const getVisiblePages = () => {
    if (totalPages <= 30) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const visible: (number | string)[] = [];
    if (page <= 10) {
      visible.push(...Array.from({ length: 10 }, (_, i) => i + 1));
      visible.push("...");
      visible.push(totalPages);
    } else if (page > totalPages - 10) {
      visible.push(1, "...");
      visible.push(...Array.from({ length: 10 }, (_, i) => totalPages - 10 + 1 + i));
    } else {
      visible.push(1, "...");
      visible.push(page - 1, page, page + 1);
      visible.push("...", totalPages);
    }
    return visible;
  };

  useEffect(() => {
    if (selectedTenant)
      fetchProfiles(selectedTenant)
  }, [selectedTenant])

  useEffect(() => {
    const updated: { [key: string]: boolean } = {};
    devices.forEach((d) => {
      updated[d.id] = !!d.recordingPathEnabled;
      if (loadingDevices[d.id] && d.agentUpgradeAvailable === false) {
        setLoadingDevices((prev) => ({ ...prev, [d.id]: false }));
      }

    });
    setRecordingState(updated);
  }, [devices]);

  useEffect(() => {
    if (!lastSelectedDevice) setSelectedDevices(new Set());
  }, [lastSelectedDevice])

  useEffect(() => {
    const saved = localStorage.getItem(PROFILE_UPDATE_STORAGE_KEY);
    if (saved) {
      try {
        const parsed: Record<string, number> = JSON.parse(saved);
        const now = Date.now();
        const validEntries = Object.entries(parsed).filter(([, expiry]) => expiry > now);
        const validExpiryMap = Object.fromEntries(validEntries);

        profileUpdateExpiryRef.current = validExpiryMap;

        if (validEntries.length > 0) {
          const initialUpdatingMap = Object.fromEntries(validEntries.map(([deviceId]) => [deviceId, true]));
          setProfileUpdatingDevices(initialUpdatingMap);

          validEntries.forEach(([deviceId, expiry]) => {
            const remainingTime = expiry - now;

            profileUpdateTimersRef.current[deviceId] = setTimeout(() => {
              setProfileUpdatingDevices((prev) => ({ ...prev, [deviceId]: false }));
              delete profileUpdateTimersRef.current[deviceId];
              delete profileUpdateExpiryRef.current[deviceId];
              localStorage.setItem(PROFILE_UPDATE_STORAGE_KEY, JSON.stringify(profileUpdateExpiryRef.current));
            }, remainingTime);
          });
        }

        localStorage.setItem(PROFILE_UPDATE_STORAGE_KEY, JSON.stringify(validExpiryMap));
      } catch (error) {
        localStorage.removeItem(PROFILE_UPDATE_STORAGE_KEY);
      }
    }

    return () => {
      Object.values(profileUpdateTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    };
  }, [PROFILE_UPDATE_STORAGE_KEY]);


  return (
    <div className="min-w-0 space-y-6">
      {tableSections.map(({ platform, label, icon: Icon, devices: platformDevices }) => {
        if (platform === "other" && (isLoading || platformDevices.length === 0)) return null;

        return (
          <section
            key={platform}
            aria-labelledby={`endpoint-platform-${platform}`}
            aria-busy={isLoading}
            className="endpoint-elevated-card endpoint-platform-card bg-card rounded-2xl border border-border/50 overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 pt-5 pb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <h2 id={`endpoint-platform-${platform}`} className="text-base font-semibold text-foreground">{label}</h2>
              {isLoading ? (
                <Skeleton className="h-5 w-8 rounded-full" />
              ) : (
                <Badge variant="secondary" className="rounded-full px-2.5 py-0.5 text-xs font-medium">{platformDevices.length}</Badge>
              )}
              {totalPages > 1 && !isLoading && (
                <span className="ml-auto text-xs text-muted-foreground">On this page</span>
              )}
            </div>
            <Table className="min-w-[1080px] table-fixed">
              <colgroup>
                <col className="w-[3%]" />
                <col className="w-[24%]" />
                <col className="w-[11%]" />
                <col className="w-[9%]" />
                <col className="w-[12%]" />
                <col className="w-[15%]" />
                <col className="w-[12%]" />
                <col className="w-[14%]" />
              </colgroup>
              <TableHeader>
                <TableRow className="endpoint-platform-head">
                  <TableHead className="w-12">
                    <Checkbox
                      disabled
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                      data-testid="checkbox-select-all"
                    />
                  </TableHead>
                  <TableHead className="font-medium text-[11px] text-muted-foreground tracking-widest uppercase h-12">DEVICE</TableHead>
                  <TableHead className="font-medium text-[11px] text-muted-foreground tracking-widest uppercase h-12">IP</TableHead>
                  <TableHead className="font-medium text-[11px] text-muted-foreground tracking-widest uppercase h-12">TAGS</TableHead>
                  <TableHead className="font-medium text-[11px] text-muted-foreground tracking-widest uppercase h-12">PROFILE</TableHead>
                  <TableHead className="font-medium text-[11px] text-muted-foreground tracking-widest uppercase h-12">OPERATING MODE</TableHead>
                  <TableHead className="font-medium text-[11px] text-muted-foreground tracking-widest uppercase h-12">MANAGEMENT</TableHead>
                  <TableHead className="font-medium text-[11px] text-muted-foreground tracking-widest uppercase h-12 text-right">ACCESS</TableHead>          </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? Array.from({ length: 2 }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="w-full h-4" />
                      </TableCell>
                    ))}
                  </TableRow>)) : platformDevices.length === 0 ? (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                        No {platform === "all" ? "" : `${label} `}devices {totalPages > 1 ? "on this page." : "match the current filters."}
                      </TableCell>
                    </TableRow>
                  ) : platformDevices.map((device) => {
                    const showAll = loadIpAddresses[device.id];
                    const displayedIPs = showAll ? device.ipAddresses : device.ipAddresses.slice(0, 2);
                    return (
                      <TableRow
                        key={device.id}
                        className="endpoint-device-row group cursor-pointer hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors border-b border-border/30 last:border-0"
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("button, a, input, [role='option'], [role='menuitem']")) return;
                          if (window.getSelection()?.toString()) return;
                          handleDeviceClick(device);
                        }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedDevices.has(device.id)}
                            onCheckedChange={() => handleDeviceSelectSingle(device.id)}
                          />
                        </TableCell>

                        <TableCell className="py-3">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center justify-center w-[42px] h-[42px] rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shrink-0">
                              {device.isManuallyAddedMachine ? <Monitor className="w-5 h-5 text-muted-foreground" /> : getOSIcon(device.os)}
                            </div>

                            <div className="space-y-1">
                              <div className="flex items-center space-x-2.5 text-sm font-semibold">
                                <div className="truncate max-w-[250px] text-foreground hover:text-primary transition-colors cursor-pointer"
                                  title={device.friendlyName ? device.friendlyName : device.name}>
                                  {device.friendlyName ? device.friendlyName : device.name}
                                </div>

                                {device.isGateway && (
                                  <div title="Gateway configured">
                                    <Router className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  </div>
                                )}

                                {device.agentUpgradeAvailable && (
                                  <div title="Agent upgrade available" onClick={(e) => { e.stopPropagation(); upgradeMachine(device.id); }}>
                                    {loadingDevices[device.id] ? (
                                      <Loader2 className="w-4 h-4 text-green-600 dark:text-green-400 animate-spin" />
                                    ) : (
                                      <ArrowUp className="w-4 h-4 text-emerald-500" />
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="text-[11px] font-medium text-muted-foreground">
                                OS: <span className="text-foreground/80">{device.os}</span>
                              </div>

                              {/* Host Name */}
                              {device.friendlyName && (
                                <div className="text-xs text-muted-foreground">
                                  Host Name: {device.name}
                                </div>
                              )}

                              {device.isManuallyAddedMachine ?
                                <>
                                  <div className="flex items-center gap-2">
                                    <div className="text-xs text-muted-foreground">
                                      Authentication : {device.protocol}
                                    </div>
                                    <StatusIndicator lastSeen={device?.lastSeen || "offline"} showDot isManualDevice={true} />
                                  </div>
                                </>
                                : <>
                                  <div className="flex items-center gap-2">
                                    <div className="text-[11px] font-medium text-muted-foreground">
                                      Agent version: <span className="text-foreground/80">{device.agentVersion || "N/A"}</span>
                                    </div>
                                    {!(device.installTag && device.installTag.length > 0) && !(device.categories && device.categories.length > 0) && (
                                      <StatusIndicator lastSeen={device?.lastSeen || "offline"} showDot isManualDevice={device?.isManuallyAddedMachine || false} />
                                    )}
                                  </div>
                                  {device.installTag && device.installTag.length > 0 &&
                                    <div className="mt-0.5 flex items-center gap-2">
                                      <div className="text-[11px] text-muted-foreground">
                                        Install Tag: <span className="text-foreground/80 font-medium">{device.installTag || "N/A"}</span>
                                      </div>
                                      {!(device.categories && device.categories.length > 0) && (
                                        <StatusIndicator lastSeen={device?.lastSeen || "offline"} showDot isManualDevice={device?.isManuallyAddedMachine || false} />
                                      )}
                                    </div>
                                  }

                                  {/* Group */}
                                  {device.categories && device.categories.length > 0 &&
                                    <div className="flex items-center gap-2">
                                      <div className="text-xs text-muted-foreground">
                                        Group: {device.categories[0].categoryName}
                                      </div>
                                      <StatusIndicator lastSeen={device?.lastSeen || "offline"} showDot isManualDevice={device?.isManuallyAddedMachine || false} />
                                    </div>}
                                </>}
                              {profileUpdatingDevices[device.id] && (
                                <div className="text-xs text-grey text-center flex items-center justify-center gap-1 w-44">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  <span>Please wait. Updating profile</span>
                                </div>
                              )}
                            </div>
                          </div>

                        </TableCell>

                        <TableCell className="font-mono text-xs text-muted-foreground py-3">
                          <div className="space-y-1">
                            {displayedIPs.map((ip, i) => (
                              <div key={i} className="flex items-center text-[13px]">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600 mr-2.5 shrink-0" />
                                {ip}
                              </div>
                            ))}
                          </div>

                          {device.ipAddresses.length > 2 && (
                            <button
                              onClick={() => toggleShowMore(device.id)}
                              className="text-[11px] font-medium text-primary hover:text-primary/80 transition-colors mt-2"
                            >
                              {showAll ? "Show less" : `Show ${device.ipAddresses.length - 2} more`}
                            </button>
                          )}
                        </TableCell>

                        <TableCell className="py-3">
                          <div className="flex flex-wrap gap-1.5 max-w-[180px]">
                            {device.tags.map((tag, i) => (
                              <Badge key={i} variant="secondary" className="no-default-hover-elevate px-2 py-0.5 text-[10px] font-medium bg-card text-foreground/80 border border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.08)] hover:bg-card">
                                {tag.name}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>



                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DeviceModeDropdown
                            device={device}
                            role={role}
                            selectedTenant={selectedTenant}
                            fetchDeviceList={fetchDeviceList}
                            profiles={profiles}
                            disabled={!!profileUpdatingDevices[device.id]}
                            onModeUpdate={triggerProfileUpdatingState}
                          />
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DeviceOperatingMode
                            device={device}
                            role={role}
                            selectedTenant={selectedTenant}
                            fetchDeviceList={fetchDeviceList}
                            disabled={!!profileUpdatingDevices[device.id]}
                            onModeUpdate={triggerProfileUpdatingState}
                          />
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <PublishComponent
                            entityName={device.name}
                            entityType="device"
                            availableRoles={roles || []}
                            publishedRoles={device.publishedroleIds || []}
                            onPublish={(selectedRoles) => handlePublish(device.id, selectedRoles)}
                          />
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()} className="py-3">
                          <div className="flex flex-col items-end gap-2.5">
                            <div className="flex items-center gap-2">
                              {!isDeviceWindows(device) && !device?.isManuallyAddedMachine && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedDeviceForSSH(device);
                                    setShowSSHDialog(true);
                                  }}
                                  data-testid={`button-settings-${device.id}`}
                                >
                                  <Settings className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="default"
                                size="sm"
                                className="h-[34px] px-2.5 shadow-sm font-medium rounded-lg"
                                data-testid={`button-connect-${device.id}`}
                                disabled={
                                  deviceLoadingState[device.id]?.loading ||
                                  !device?.isNebulaEnabled ||
                                  (connectAccessDisable(device) &&
                                    role !== "TENANT_USER") ||
                                  (!isDeviceWindows(device) &&
                                    !connectButtonEnabled(device) &&
                                    !device?.isManuallyAddedMachine)
                                }
                                onClick={() => handleConnect(device)}>
                                Connect
                                {deviceLoadingState[device.id]?.loading && <Loader2 className="w-4 h-4 ml-1.5 animate-spin" />}
                              </Button>
                            </div>

                            <div className="flex items-center justify-end w-full gap-2 px-1">
                              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Recording</span>
                              <Switch
                                checked={recordingState[device.id] ?? device.recordingPathEnabled}
                                onCheckedChange={(checked) => handleRecordingChange(device.id, checked, device)}
                                data-testid={`switch-recording-${device.id}`}
                                className="scale-75"
                              />
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>
          </section>
        );
      })}

      {totalPages > 1 && (
        <div className="endpoint-elevated-card flex flex-wrap items-center justify-center gap-2 p-4 rounded-2xl bg-card border border-border/50">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="p-2"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          {getVisiblePages().map((p, idx) =>
            p === "..." ? (
              <span key={`dots-${idx}`} className="px-2 text-xs text-muted-foreground">
                ...
              </span>
            ) : (
              <Button
                key={p}
                variant={page === p ? "default" : "outline"}
                size="sm"
                onClick={() => setPage(Number(p))}
                className="w-8 h-8 p-0"
              >
                {p}
              </Button>
            )
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page === totalPages}
            className="p-2"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}


      <SSHSettingsDialog
        open={showSSHDialog}
        onOpenChange={setShowSSHDialog}
        device={selectedDeviceForSSH}
        onSave={(deviceId, username, port) => {
          configureDeviceUsername?.(userId, username, role, deviceId, port);
          setShowSSHDialog(false);
        }}
      />

      <AdminWindowsConnectDialog
        open={showAdminConnectDialog}
        onOpenChange={setShowAdminConnectDialog}
        deviceName={device?.name}
        deviceId={device?.id || ""}
        credentials={credentials || []}
        onConnect={(deviceId, username, mode, label) => {
          if (device) {
            onConnect?.(device, mode, label);
          }
          setShowAdminConnectDialog(false);
        }
        }
      />

    </div>
  );
}
